import firestore from '@react-native-firebase/firestore';
import storage from '@react-native-firebase/storage';
import { CreateDepositData, Deposit, DepositStatistics } from '../types/Deposit';
import { ErrorLogger } from '../utils/errorLogger';
import { mapDepositDoc } from '../utils/mappers';
import { aggregateDeposits } from '../utils/aggregators';
import { thisWeek } from '../utils/dateRange';

const DEPOSITS_COLLECTION = 'deposits';
const STORAGE_PATH = 'deposits/receipts';

export class DepositService {
  private static getDepositsCollection() {
    return firestore().collection(DEPOSITS_COLLECTION);
  }

  /**
   * Create a new deposit
   */
  static async createDeposit(
    userId: string,
    depositData: CreateDepositData,
  ): Promise<Deposit> {
    try {
      const now = firestore.Timestamp.now();
      const depositTimestamp = firestore.Timestamp.fromDate(depositData.depositDate);

      const depositDoc = {
        userId,
        amount: depositData.amount,
        description: depositData.description || null,
        receiptUrl: depositData.receiptUrl || null,
        depositDate: depositTimestamp,
        status: depositData.status,
        truck: depositData.truck || null,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await this.getDepositsCollection().add(depositDoc);

      const createdDeposit: Deposit = {
        id: docRef.id,
        userId,
        amount: depositData.amount,
        description: depositData.description,
        receiptUrl: depositData.receiptUrl,
        depositDate: depositData.depositDate,
        status: depositData.status,
        truck: depositData.truck,
        createdAt: now.toDate(),
        updatedAt: now.toDate(),
      };

      console.log('Deposit created successfully:', createdDeposit.id);
      return createdDeposit;
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.createDeposit');
      throw new Error('Failed to create deposit');
    }
  }

  /**
   * Update a deposit
   */
  static async updateDeposit(
    depositId: string,
    updateData: Partial<CreateDepositData>,
  ): Promise<void> {
    try {
      const updatePayload = {
        ...updateData,
        updatedAt: new Date(),
      };

      await this.getDepositsCollection().doc(depositId).update(updatePayload);
      console.log('Deposit updated successfully:', depositId);
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.updateDeposit');
      throw new Error('Failed to update deposit');
    }
  }

  /**
   * Delete a deposit
   */
  static async deleteDeposit(depositId: string): Promise<void> {
    try {
      // Get the deposit to check if it has a receipt
      const depositDoc = await this.getDepositsCollection().doc(depositId).get();
      const deposit = depositDoc.data() as Deposit;

      // Delete receipt from storage if it exists
      if (deposit?.receiptUrl) {
        try {
          const reference = storage().refFromURL(deposit.receiptUrl);
          await reference.delete();
          console.log('Receipt deleted from storage');
        } catch (storageError) {
          console.warn('Failed to delete receipt from storage:', storageError);
          // Continue with deposit deletion even if receipt deletion fails
        }
      }

      // Delete the deposit document
      await this.getDepositsCollection().doc(depositId).delete();
      console.log('Deposit deleted successfully:', depositId);
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.deleteDeposit');
      throw new Error('Failed to delete deposit');
    }
  }

  /**
   * Upload receipt image to Firebase Storage
   */
  static async uploadReceipt(
    userId: string,
    depositId: string,
    imageUri: string,
  ): Promise<string> {
    try {
      const fileName = `${depositId}_${Date.now()}.jpg`;
      const reference = storage().ref(`${STORAGE_PATH}/${userId}/${fileName}`);

      // Handle different URI formats
      let localUri = imageUri;
      if (imageUri.startsWith('file://')) {
        localUri = imageUri.replace('file://', '');
      }

      console.log('Uploading receipt from URI:', localUri);
      await reference.putFile(localUri);
      const downloadUrl = await reference.getDownloadURL();

      console.log('Receipt uploaded successfully:', downloadUrl);
      return downloadUrl;
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.uploadReceipt');
      console.error('Upload error details:', error);
      throw new Error('Failed to upload receipt');
    }
  }

  /**
   * Get deposits for this week (fleet-wide — deposits are a supervisor concern).
   */
  static async getDepositsThisWeek(): Promise<Deposit[]> {
    try {
      const { start: startOfWeek, end: endOfWeek } = thisWeek();

      const snapshot = await this.getDepositsCollection().get();

      const deposits: Deposit[] = snapshot.docs
        .map(doc => mapDepositDoc(doc))
        .filter(d => d.depositDate >= startOfWeek && d.depositDate <= endOfWeek)
        .sort((a, b) => b.depositDate.getTime() - a.depositDate.getTime());

      console.log(`Retrieved ${deposits.length} deposits for this week`);
      return deposits;
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.getDepositsThisWeek');
      return [];
    }
  }

  /**
   * Get deposit statistics (fleet-wide).
   */
  static async getDepositStatistics(): Promise<DepositStatistics> {
    try {
      const snapshot = await this.getDepositsCollection().get();
      const deposits: Deposit[] = snapshot.docs.map(doc => mapDepositDoc(doc));
      const stats = aggregateDeposits(deposits);
      console.log('Deposit statistics calculated:', stats);
      return stats;
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.getDepositStatistics');
      return {
        totalDeposits: 0,
        totalAmount: 0,
        pendingDeposits: 0,
        completedDeposits: 0,
        pendingAmount: 0,
        completedAmount: 0,
        weeklyTotal: 0,
        monthlyTotal: 0,
        annualTotal: 0,
      };
    }
  }

  /**
   * Subscribe to real-time updates for all fleet deposits (no owner scoping).
   */
  static subscribeToAllDeposits(
    onUpdate: (deposits: Deposit[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = this.getDepositsCollection()
      .onSnapshot(
        snapshot => {
          const deposits: Deposit[] = snapshot.docs
            .map(doc => mapDepositDoc(doc))
            .sort((a, b) => b.depositDate.getTime() - a.depositDate.getTime());
          onUpdate(deposits);
        },
        error => {
          ErrorLogger.logError(error, 'DepositService.subscribeToAllDeposits');
          if (onError) {
            onError(error as Error);
          } else {
            onUpdate([]);
          }
        },
      );

    return unsubscribe;
  }

  static async getAllDeposits(): Promise<Deposit[]> {
    try {
      const snapshot = await this.getDepositsCollection().get();
      return snapshot.docs.map(doc => mapDepositDoc(doc));
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.getAllDeposits');
      return [];
    }
  }

  /**
   * Get total deposited amount (for cash at hand calculation)
   */
  static async getTotalDepositedAmount(): Promise<number> {
    try {
      const query = this.getDepositsCollection().where('status', '==', 'completed');

      const snapshot = await query.get();

      const totalDeposited = snapshot.docs.reduce((total, doc) => {
        const data = doc.data();
        return total + (data.amount || 0);
      }, 0);

      console.log('Total deposited amount calculated:', totalDeposited);
      return totalDeposited;
    } catch (error) {
      ErrorLogger.logError(error, 'DepositService.getTotalDepositedAmount');
      return 0;
    }
  }
}

export default DepositService;